import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckRounded,
  Close,
  Storefront,
  WhatsApp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';
import { createWebsitePlanOrder, verifyWebsitePlanPayment } from '../websitePlanService';
import { PALETTE_LIGHT } from '../../theme/tokens';

// ── Razorpay script loader ────────────────────────────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PHONE_RE = /^[6-9]\d{9}$/;
const WA_NUMBER = '918595675155';
const PAGE_SLUG = 'join-us-as-library';

// ── Success screen ────────────────────────────────────────────────────────────
function SuccessScreen({ purchase, plan, onDismiss }) {
  const theme = useTheme();
  const orange = theme.palette.brand.orange.main;

  return (
    <Box sx={{ textAlign: 'center', py: 3, px: 1 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${orange}, ${theme.palette.brand.golden.main})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <CheckRounded sx={{ color: '#fff', fontSize: 36 }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={0.75} color="text.primary">
        Payment successful!
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} lineHeight={1.7}>
        Thank you, <strong>{purchase.buyer_name}</strong>. Your order for{' '}
        <strong>{plan.name}</strong> has been received.
      </Typography>

      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
          textAlign: 'left',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Plan
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.primary">
            {plan.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Library
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.primary">
            {purchase.library_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Amount paid
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: orange }}>
            {purchase.amount_display}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3} lineHeight={1.7}>
        Our team will reach out within 24 hours to get your library set up.
      </Typography>

      <Button
        variant="contained"
        fullWidth
        onClick={onDismiss}
        sx={{
          background: `linear-gradient(135deg, ${orange}, ${theme.palette.brand.golden.main})`,
          color: '#fff',
          fontWeight: 700,
          py: 1.5,
          borderRadius: 3,
          boxShadow: 'none',
        }}
      >
        Done
      </Button>
    </Box>
  );
}

SuccessScreen.propTypes = {
  purchase: PropTypes.shape({
    buyer_name: PropTypes.string,
    library_name: PropTypes.string,
    amount_display: PropTypes.string,
  }).isRequired,
  plan: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

// ── Checkout dialog ───────────────────────────────────────────────────────────
function CheckoutDialog({ open, tier, ctaLabel, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const EMPTY_FORM = {
    buyer_name: '',
    buyer_phone: '',
    buyer_email: '',
    library_name: '',
  };

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successData, setSuccessData] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setApiError('');
    setSuccessData(null);
    onClose();
  };

  const validate = () => {
    const next = {};
    if (!form.buyer_name.trim()) next.buyer_name = 'Name is required';
    if (!PHONE_RE.test(form.buyer_phone.trim()))
      next.buyer_phone = 'Enter a valid 10-digit Indian mobile number (starts with 6-9)';
    if (form.buyer_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.buyer_email.trim()))
      next.buyer_email = 'Enter a valid email address';
    if (!form.library_name.trim()) next.library_name = 'Library name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError('');

    try {
      const orderData = await createWebsitePlanOrder({
        page_slug: PAGE_SLUG,
        tier_id: tier.id,
        buyer_name: form.buyer_name.trim(),
        buyer_phone: form.buyer_phone.trim(),
        buyer_email: form.buyer_email.trim() || undefined,
        library_name: form.library_name.trim(),
      });

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setApiError('Failed to load payment gateway. Please check your connection and try again.');
        setSubmitting(false);
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'BookMyLibrary',
        description: `${tier.name} — Library Plan`,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.prefill?.name || form.buyer_name.trim(),
          email: orderData.prefill?.email || form.buyer_email.trim() || '',
          contact: orderData.prefill?.contact || form.buyer_phone.trim(),
        },
        theme: { color: PALETTE_LIGHT.primary.main },
        handler: async function (response) {
          try {
            const verifyResult = await verifyWebsitePlanPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSuccessData(verifyResult);
          } catch (verifyErr) {
            setApiError(
              'Payment received but verification failed. Please contact us with your payment ID: ' +
                response.razorpay_payment_id
            );
          }
          setSubmitting(false);
        },
        modal: {
          ondismiss: function () {
            setSubmitting(false);
            setApiError(
              'Payment window was closed. Click "' +
                (ctaLabel || 'Get started') +
                '" again to complete your purchase.'
            );
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setSubmitting(false);
        setApiError(response.error?.description || 'Payment failed. Please try again.');
      });
      rzp.open();
    } catch (err) {
      setSubmitting(false);
      setApiError(err.message || 'Something went wrong. Please try again.');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(27,54,93,0.03)',
    },
  };

  if (!tier) return null;

  const priceDisplay = tier.price ? `₹${tier.price}` : 'Free';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: 'background.paper',
          boxShadow: isDark
            ? '0 24px 64px rgba(0,0,0,0.6)'
            : '0 24px 64px rgba(27,54,93,0.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {successData ? 'Order confirmed' : `Get started with ${tier.name}`}
          </Typography>
          {!successData && (
            <Typography variant="caption" color="text.secondary">
              {priceDisplay} {tier.billing_period}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={submitting}
          aria-label="Close dialog"
          sx={{ color: 'text.secondary' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
        {successData ? (
          <SuccessScreen
            purchase={successData.purchase}
            plan={successData.plan_snapshot}
            onDismiss={handleClose}
          />
        ) : (
          <Box>
            {apiError && (
              <Box
                sx={{
                  bgcolor: 'error.main',
                  color: '#fff',
                  px: 2,
                  py: 1.5,
                  borderRadius: '10px',
                  mb: 2.5,
                  fontSize: '0.82rem',
                  lineHeight: 1.5,
                }}
                role="alert"
              >
                {apiError}
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Your name"
                  fullWidth
                  value={form.buyer_name}
                  onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                  error={!!errors.buyer_name}
                  helperText={errors.buyer_name}
                  disabled={submitting}
                  inputProps={{ 'aria-label': 'Your name' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Mobile number"
                  fullWidth
                  value={form.buyer_phone}
                  onChange={(e) =>
                    setForm({ ...form, buyer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                  }
                  error={!!errors.buyer_phone}
                  helperText={errors.buyer_phone || 'Indian 10-digit number, e.g. 9876543210'}
                  disabled={submitting}
                  inputMode="numeric"
                  inputProps={{ maxLength: 10, 'aria-label': 'Mobile number' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email (optional)"
                  fullWidth
                  type="email"
                  value={form.buyer_email}
                  onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                  error={!!errors.buyer_email}
                  helperText={errors.buyer_email}
                  disabled={submitting}
                  inputProps={{ 'aria-label': 'Email address' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Library name"
                  fullWidth
                  value={form.library_name}
                  onChange={(e) => setForm({ ...form, library_name: e.target.value })}
                  error={!!errors.library_name}
                  helperText={errors.library_name || 'The name of your study library'}
                  disabled={submitting}
                  inputProps={{ 'aria-label': 'Library name' }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
              size="large"
              sx={{
                mt: 3,
                background: `linear-gradient(135deg, ${orange}, ${golden})`,
                color: '#fff',
                fontWeight: 700,
                py: 1.6,
                fontSize: '0.95rem',
                borderRadius: 3,
                boxShadow: `0 8px 24px rgba(242,101,34,0.35)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                  boxShadow: '0 12px 32px rgba(242,101,34,0.48)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(27,54,93,0.08)',
                  color: 'text.disabled',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {submitting ? (
                <CircularProgress size={22} thickness={4} sx={{ color: 'inherit' }} />
              ) : (
                `Pay ${priceDisplay}`
              )}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

CheckoutDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  tier: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.string,
    billing_period: PropTypes.string,
  }),
  ctaLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

// ── Tier card ─────────────────────────────────────────────────────────────────
function TierCard({ tier, index, ctaLabel, onSelect }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;
  const navy = theme.palette.brand.navy.main;

  const isHighlight = !!tier.highlight;
  const priceDisplay = tier.price ? `₹${tier.price}` : 'Free';

  const cardBg = isHighlight
    ? isDark
      ? `linear-gradient(145deg, rgba(242,101,34,0.14) 0%, rgba(27,54,93,0.80) 100%)`
      : `linear-gradient(145deg, rgba(242,101,34,0.07) 0%, rgba(255,255,255,1) 100%)`
    : isDark
    ? 'rgba(27,54,93,0.45)'
    : theme.palette.background.paper;

  const cardBorder = isHighlight
    ? isDark
      ? '1px solid rgba(242,101,34,0.30)'
      : '1px solid rgba(242,101,34,0.22)'
    : isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.08)';

  return (
    <RevealOnScroll delay={Math.min(index * 0.09, 0.45)}>
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        style={{ height: '100%' }}
      >
        <Box
          sx={{
            position: 'relative',
            p: { xs: 3.5, md: 4 },
            borderRadius: '20px',
            background: cardBg,
            border: cardBorder,
            boxShadow: isHighlight
              ? isDark
                ? '0 12px 48px rgba(0,0,0,0.45)'
                : '0 12px 48px rgba(242,101,34,0.14)'
              : isDark
              ? '0 4px 24px rgba(0,0,0,0.25)'
              : '0 4px 24px rgba(27,54,93,0.06)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'box-shadow 0.22s ease',
            '&:hover': {
              boxShadow: isHighlight
                ? isDark
                  ? '0 20px 56px rgba(0,0,0,0.55), 0 0 0 1px rgba(242,101,34,0.3)'
                  : '0 20px 56px rgba(242,101,34,0.22), 0 0 0 1px rgba(242,101,34,0.18)'
                : isDark
                ? '0 16px 48px rgba(0,0,0,0.40)'
                : '0 16px 48px rgba(27,54,93,0.12)',
            },
          }}
        >
          {/* Recommended banner */}
          {isHighlight && (
            <Box
              aria-label="Recommended plan"
              sx={{
                position: 'absolute',
                top: -1,
                left: '50%',
                transform: 'translateX(-50%)',
                px: 2,
                py: 0.5,
                background: `linear-gradient(135deg, ${orange}, ${golden})`,
                borderRadius: '0 0 12px 12px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Recommended
              </Typography>
            </Box>
          )}

          {/* Badge chip */}
          {tier.badge && (
            <Box sx={{ mb: 1.5, mt: isHighlight ? 1.5 : 0 }}>
              <Chip
                label={tier.badge}
                size="small"
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  height: 22,
                  bgcolor: isDark ? 'rgba(242,101,34,0.15)' : 'rgba(242,101,34,0.10)',
                  color: orange,
                  border: `1px solid ${isDark ? 'rgba(242,101,34,0.30)' : 'rgba(242,101,34,0.22)'}`,
                }}
              />
            </Box>
          )}

          {/* Tier name */}
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: 'text.primary',
              mb: 1,
              mt: isHighlight && !tier.badge ? 1.5 : 0,
              fontSize: { xs: '1rem', md: '1.1rem' },
            }}
          >
            {tier.name}
          </Typography>

          {/* Price */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.4rem', md: '2.75rem' },
                lineHeight: 1,
                color: orange,
              }}
            >
              {priceDisplay}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 2.5 }}
          >
            {tier.billing_period}
          </Typography>

          <Divider
            sx={{
              mb: 2.5,
              borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(27,54,93,0.08)',
            }}
          />

          {/* Features */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1, mb: 3.5 }}>
            {(tier.features || []).map((feature, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CheckRounded
                  sx={{ color: orange, fontSize: 17, flexShrink: 0, mt: 0.1 }}
                  aria-hidden="true"
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontSize: '0.83rem', lineHeight: 1.5 }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTA */}
          <Button
            variant={isHighlight ? 'contained' : 'outlined'}
            fullWidth
            size="large"
            onClick={() => onSelect(tier)}
            aria-label={`${ctaLabel || 'Get started'} with ${tier.name}`}
            sx={
              isHighlight
                ? {
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    color: '#fff',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                      boxShadow: '0 12px 32px rgba(242,101,34,0.48)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }
                : {
                    color: isDark ? 'rgba(255,255,255,0.8)' : navy,
                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(27,54,93,0.20)',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 3,
                    '&:hover': {
                      borderColor: orange,
                      color: orange,
                      bgcolor: isDark ? 'rgba(242,101,34,0.07)' : 'rgba(242,101,34,0.05)',
                    },
                    transition: 'all 0.2s ease',
                  }
            }
          >
            {ctaLabel || 'Get started'}
          </Button>
        </Box>
      </motion.div>
    </RevealOnScroll>
  );
}

TierCard.propTypes = {
  tier: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    billing_period: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string),
    badge: PropTypes.string,
    highlight: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  ctaLabel: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

// ── Main section ──────────────────────────────────────────────────────────────
function PriceTiersSection({ eyebrow, title, subtitle, footnote, ctaLabel, tiers }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;

  const safeTiers = Array.isArray(tiers) ? tiers : [];

  const [selectedTier, setSelectedTier] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSelect = (tier) => {
    setSelectedTier(tier);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Keep selectedTier briefly so dialog exit animation is smooth
    setTimeout(() => setSelectedTier(null), 300);
  };

  return (
    <SectionContainer
      id="pricing"
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      sx={{
        background: isDark
          ? `radial-gradient(ellipse at 80% 20%, rgba(242,101,34,0.05) 0%, transparent 60%), ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 80% 20%, rgba(242,101,34,0.04) 0%, transparent 60%), ${theme.palette.background.default}`,
      }}
    >
      {/* Tier cards */}
      {safeTiers.length === 0 ? (
        <RevealOnScroll>
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '20px',
              border: isDark
                ? '1px dashed rgba(255,255,255,0.12)'
                : '1px dashed rgba(27,54,93,0.14)',
              background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(27,54,93,0.02)',
              textAlign: 'center',
              maxWidth: 520,
              mx: 'auto',
            }}
          >
            <Storefront
              sx={{ fontSize: 48, color: orange, opacity: 0.5, mb: 2 }}
              aria-hidden="true"
            />
            <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
              Plans coming soon
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3} lineHeight={1.75}>
              We are finalizing our pricing. Reach out on WhatsApp to learn more and get early
              access.
            </Typography>
            <Button
              component="a"
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
                'Hi! I want to know more about listing my library on BookMyLibrary.'
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<WhatsApp />}
              sx={{
                background: '#25D366',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                py: 1.4,
                '&:hover': { background: '#128C7E' },
              }}
            >
              Chat with us
            </Button>
          </Box>
        </RevealOnScroll>
      ) : (
        <Grid container spacing={3} justifyContent={safeTiers.length < 3 ? 'center' : undefined}>
          {safeTiers.map((tier, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={safeTiers.length === 1 ? 5 : safeTiers.length === 2 ? 5 : 4}
              key={tier.id}
            >
              <TierCard
                tier={tier}
                index={index}
                ctaLabel={ctaLabel}
                onSelect={handleSelect}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Footnote */}
      {footnote && safeTiers.length > 0 && (
        <RevealOnScroll delay={0.2}>
          <Typography
            variant="caption"
            display="block"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mt: { xs: 4, md: 5 },
              fontSize: '0.78rem',
              lineHeight: 1.7,
              maxWidth: 560,
              mx: 'auto',
              opacity: 0.8,
            }}
          >
            {footnote}
          </Typography>
        </RevealOnScroll>
      )}

      {/* Checkout dialog */}
      <CheckoutDialog
        open={dialogOpen}
        tier={selectedTier}
        ctaLabel={ctaLabel}
        onClose={handleCloseDialog}
      />
    </SectionContainer>
  );
}

PriceTiersSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  footnote: PropTypes.string,
  ctaLabel: PropTypes.string,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      billing_period: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(PropTypes.string),
      badge: PropTypes.string,
      highlight: PropTypes.bool,
    })
  ),
};

PriceTiersSection.defaultProps = {
  eyebrow: 'Pricing',
  title: 'Choose your plan.',
  subtitle: 'Transparent pricing. Everything your library needs to grow.',
  footnote: '',
  ctaLabel: 'Get started',
  tiers: [],
};

export default PriceTiersSection;
